/**
 * The SOPs tab's words and small pure rules (plan docs/plans/lms-plan.md
 * 5.6, 11, section 13; WP-46). No React, no I/O.
 */
import { format } from 'date-fns';
import type { PillTone } from '../../components/StatusPill';
import { formatDay } from '../../components/pickers/shared';
import type { SopDetailSop, SopDetailVersion, SopStatus, SopVersionStatus } from '../../api/sops';

/** The tab's URL; the tracker of one SOP is the same route with ?sop=<id> (the tab route has no subtree). */
export const SOPS_PATH = '/lms/sops';
export const SOP_QUERY_KEY = 'sop';

export function sopHref(sopId: string): string {
  return `${SOPS_PATH}?${SOP_QUERY_KEY}=${encodeURIComponent(sopId)}`;
}

/** 5.6 version statuses in words, with the section 13 rule: a colour always with its word. */
export const VERSION_STATUS_LABELS: Record<SopVersionStatus, string> = {
  draft: 'Draft',
  effective: 'Effective',
  superseded: 'Superseded',
  retired: 'Retired',
};

export const VERSION_STATUS_TONES: Record<SopVersionStatus, PillTone> = {
  draft: 'primary',
  effective: 'green',
  superseded: 'gray',
  retired: 'gray',
};

export const SOP_STATUS_LABELS: Record<SopStatus, string> = {
  active: 'Active',
  retired: 'Retired',
};

/** 11.2 step 1 in words. */
export const MODE_LABELS: Record<'read_ack' | 'quiz', string> = {
  read_ack: 'Read and acknowledge',
  quiz: 'Quiz',
};

export const MODE_HINTS: Record<'read_ack' | 'quiz', string> = {
  read_ack: "People open the SOP, confirm they read it in full and sign with their password. No quiz.",
  quiz: "A quiz on the SOP. The training is created as a draft: you write the questions and publish it in the course editor, then send it to people from here.",
};

/**
 * A day as the module shows it ("1 Sep 2026"). A date-only value (5.6
 * effective_on is a date) is read as that calendar day, never as UTC
 * midnight, which would show the day before west of Greenwich; an instant is
 * shown in the viewer's time zone.
 */
export function formatSopDay(value: string | null | undefined): string {
  if (!value) return '\u2014';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return Number.isNaN(d.getTime()) ? '\u2014' : format(d, 'd MMM yyyy');
  }
  return formatDay(value);
}

/** A file size in words (KB below a megabyte, else MB with one decimal). */
export function fileSize(bytes: number | null | undefined): string {
  if (typeof bytes !== 'number' || !Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024)).toLocaleString()} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** The file type in words from its mime (5.6 allows PDF and DOCX only). */
export function fileKind(mime: string | null | undefined): string {
  if (mime === 'application/pdf') return 'PDF';
  if (mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return 'DOCX';
  return 'File';
}

/** The first characters of a hash, for a table cell (the full value sits in the title). */
export function shortHash(sha256: string | null | undefined): string {
  return typeof sha256 === 'string' && sha256.length > 12 ? `${sha256.slice(0, 12)}\u2026` : sha256 || '';
}

/**
 * What the tracker offers for sending a version (11.2, 18.25):
 *   'send'            an effective version with no linked training yet (or a discarded one): the four steps
 *   'effective_first' a draft with no linked training: make it effective first, then send it. The
 *                     library's training counts and a learner's "acknowledged" are keyed to the
 *                     EFFECTIVE version, so a draft sent for training read "Not sent for training"
 *                     until it was made effective (docs/plans/sop-import-plan.md 4.3 D, 4.4 item 2).
 *                     The server still accepts a draft send (11.2); the tracker no longer offers it.
 *   'finish_quiz'     a quiz-mode draft is waiting in the course editor
 *   'send_people'     the training version is published (a quiz draft published in the editor) but nobody was sent it
 *   'sent'            a campaign went out: more people come from the Assign wizard (kind sop_update)
 *   'none'            superseded or retired versions, and retired SOPs, are not sent
 */
export type SendState = 'send' | 'effective_first' | 'finish_quiz' | 'send_people' | 'sent' | 'none';

export function sendStateOf(sop: Pick<SopDetailSop, 'status'>, version: SopDetailVersion): SendState {
  if (sop.status !== 'active') return 'none';
  if (version.campaign || version.sent_campaign_id) return 'sent';
  if (version.status !== 'draft' && version.status !== 'effective') return 'none';
  const cv = version.course_version ?? null;
  if (cv && cv.status === 'draft') return 'finish_quiz';
  if (cv && cv.status === 'published') return 'send_people';
  if (cv && cv.status === 'superseded') return 'none';
  return version.status === 'draft' ? 'effective_first' : 'send';
}
